import React, { useState, useEffect } from 'react';
import AddRanchGirlForm from './AddRanchGirlForm.tsx';
import EditRanchGirlForm from './EditRanchGirlForm.tsx';

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  age: number;
  skillLevel: 'Non Basic' | 'Basic' | 'Third Class' | 'Second Class' | 'First Class';
  horseAssigned: string;
  assignedHorse?: {
    id: string;
    name: string;
  };
  boardPosition?: string;
  isActive: boolean;
  graduationYear: number;
  memberSince: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  allergies: string;
  medicalNotes: string;
  parentalConsent: boolean;
  liabilityWaiverSigned: boolean;
  family: {
    familyName: string;
    primaryContactFirstName: string;
    primaryContactLastName: string;
  };
}

export default function RanchGirlManagement() {
  const [ranchGirls, setRanchGirls] = useState<RanchGirl[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSkill, setFilterSkill] = useState<string>('all');
  const [selectedGirl, setSelectedGirl] = useState<RanchGirl | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedGirls, setSelectedGirls] = useState<Set<string>>(new Set());
  useEffect(() => {
    fetchRanchGirls();
  }, []);

  const fetchRanchGirls = async () => {
    try {
      // For now, we'll get ranch girls through families
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Extract all ranch girls from families
        const allRanchGirls: RanchGirl[] = [];
        data.data.forEach((family: any) => {
          if (family.ranchGirls) {
            family.ranchGirls.forEach((girl: any) => {
              allRanchGirls.push({
                ...girl,
                family: {
                  familyName: family.familyName,
                  primaryContactFirstName: family.primaryContactFirstName,
                  primaryContactLastName: family.primaryContactLastName
                }
              });
            });
          }
        });
        setRanchGirls(allRanchGirls);
      }
    } catch (error) {
      console.error('Error fetching ranch girls:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (ranchGirls.length === 0) {
      alert('No ranch girl data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'First Name',
      'Last Name',
      'Age',
      'Date of Birth',
      'Family Name',
      'Primary Contact',
      'Skill Level',
      'Horse Assigned',
      'Board Position',
      'Graduation Year',
      'Member Since',
      'Emergency Contact Name',
      'Emergency Contact Phone',
      'Allergies',
      'Medical Notes',
      'Parental Consent',
      'Liability Waiver Signed',
      'Active Status'
    ];

    // Convert ranch girls data to CSV rows
    const csvRows = ranchGirls.map(girl => [
      girl.firstName,
      girl.lastName,
      girl.age || 'N/A',
      girl.dateOfBirth || 'N/A',
      girl.family?.familyName || 'N/A',
      girl.family ? `${girl.family.primaryContactFirstName} ${girl.family.primaryContactLastName}` : 'N/A',
      girl.skillLevel,
      girl.assignedHorse?.name || 'N/A',
      girl.boardPosition || 'None',
      girl.graduationYear || 'N/A',
      girl.memberSince || 'N/A',
      girl.emergencyContactName || 'N/A',
      girl.emergencyContactPhone || 'N/A',
      girl.allergies || 'N/A',
      girl.medicalNotes || 'N/A',
      girl.parentalConsent ? 'Yes' : 'No',
      girl.liabilityWaiverSigned ? 'Yes' : 'No',
      girl.isActive ? 'Active' : 'Inactive'
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `GGAA_RanchGirls_Export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSkillColor = (skill: string) => {
    switch (skill) {
      case 'Non Basic': return 'bg-gray-100 text-gray-800';
      case 'Basic': return 'bg-yellow-100 text-yellow-800';
      case 'Third Class': return 'bg-blue-100 text-blue-800';
      case 'Second Class': return 'bg-green-100 text-green-800';
      case 'First Class': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleGirlSelection = (girlId: string) => {
    const newSelection = new Set(selectedGirls);
    if (newSelection.has(girlId)) {
      newSelection.delete(girlId);
    } else {
      newSelection.add(girlId);
    }
    setSelectedGirls(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedGirls.size === filteredRanchGirls.length) {
      setSelectedGirls(new Set());
    } else {
      setSelectedGirls(new Set(filteredRanchGirls.map(g => g.id)));
    }
  };

  const handleMassDelete = async () => {
    if (selectedGirls.size === 0) {
      alert('Please select ranch girls to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedGirls.size} ranch ${selectedGirls.size === 1 ? 'girl' : 'girls'}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/ranchgirls/bulk-delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          girlIds: Array.from(selectedGirls)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully deleted ${selectedGirls.size} ranch ${selectedGirls.size === 1 ? 'girl' : 'girls'}`);
        setSelectedGirls(new Set());
        fetchRanchGirls();
      } else {
        alert('Error deleting ranch girls: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting ranch girls:', error);
      alert('Error deleting ranch girls');
    }
  };

  const filteredRanchGirls = filterSkill === 'all' 
    ? ranchGirls.filter(girl => girl.isActive)
    : ranchGirls.filter(girl => girl.isActive && girl.skillLevel === filterSkill);

  const skillDistribution = {
    nonBasic: ranchGirls.filter(g => g.skillLevel === 'Non Basic' && g.isActive).length,
    basic: ranchGirls.filter(g => g.skillLevel === 'Basic' && g.isActive).length,
    thirdClass: ranchGirls.filter(g => g.skillLevel === 'Third Class' && g.isActive).length,
    secondClass: ranchGirls.filter(g => g.skillLevel === 'Second Class' && g.isActive).length,
    firstClass: ranchGirls.filter(g => g.skillLevel === 'First Class' && g.isActive).length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  const activeCount = ranchGirls.filter(g => g.isActive).length;

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ranch Girl Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
            Manage all ranch girls and their progress
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 w-full">
          {selectedGirls.size > 0 && (
            <button
              type="button"
              onClick={handleMassDelete}
              className="w-full sm:w-auto bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center min-h-[44px]"
            >
              <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected ({selectedGirls.size})
            </button>
          )}
          <button
            type="button"
            onClick={exportToCSV}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center min-h-[44px]"
          >
            <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto bg-ggaa-green text-white px-4 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium min-h-[44px]"
          >
            + Add New Ranch Girl
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20 col-span-2 sm:col-span-1">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Total Active</h3>
          <p className="text-2xl sm:text-3xl font-bold text-ggaa-green">{activeCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Non Basic</h3>
          <p className="text-2xl sm:text-3xl font-bold text-gray-600">{skillDistribution.nonBasic}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Basic</h3>
          <p className="text-2xl sm:text-3xl font-bold text-yellow-600">{skillDistribution.basic}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Third Class</h3>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">{skillDistribution.thirdClass}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Second Class</h3>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">{skillDistribution.secondClass}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">First Class</h3>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">{skillDistribution.firstClass}</p>
        </div>
      </div>

      {/* Filters & bulk select */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border border-ggaa-green/20">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
          <label className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-700 shrink-0">Filter by skill level</span>
            <select
              value={filterSkill}
              onChange={(e) => setFilterSkill(e.target.value)}
              className="w-full sm:w-auto sm:min-w-[200px] border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ggaa-green min-h-[44px]"
            >
              <option value="all">All Levels</option>
              <option value="Non Basic">Non Basic</option>
              <option value="Basic">Basic</option>
              <option value="Third Class">Third Class</option>
              <option value="Second Class">Second Class</option>
              <option value="First Class">First Class</option>
            </select>
          </label>
          {filteredRanchGirls.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-600 min-h-[44px]">
              <input
                type="checkbox"
                checked={selectedGirls.size === filteredRanchGirls.length && filteredRanchGirls.length > 0}
                onChange={toggleSelectAll}
                className="h-5 w-5 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
              />
              Select all shown
              {selectedGirls.size > 0 && (
                <span className="text-gray-500">({selectedGirls.size} selected)</span>
              )}
            </label>
          )}
        </div>
        <p className="mt-2 text-xs sm:text-sm text-gray-500">
          Showing {filteredRanchGirls.length} of {activeCount} active ranch girls
        </p>
      </div>

      {/* Ranch Girls Grid */}
      {filteredRanchGirls.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg border border-ggaa-green/20 text-center py-12 px-4">
          <p className="text-gray-500 text-base sm:text-lg mb-4">
            {activeCount === 0
              ? 'No ranch girls yet.'
              : 'No ranch girls found for the selected filter.'}
          </p>
          {activeCount === 0 && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="text-ggaa-green font-medium hover:underline"
            >
              Add your first ranch girl
            </button>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {filteredRanchGirls.map((girl) => (
          <div key={girl.id} className="bg-white rounded-xl shadow-lg border border-ggaa-green/20 overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex gap-3 items-start mb-4">
                <input
                  type="checkbox"
                  checked={selectedGirls.has(girl.id)}
                  onChange={() => toggleGirlSelection(girl.id)}
                  className="mt-1 h-5 w-5 shrink-0 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                  aria-label={`Select ${girl.firstName} ${girl.lastName}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                        {girl.firstName} {girl.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{girl.family.familyName} Family</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getSkillColor(girl.skillLevel)}`}>
                      {girl.skillLevel}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Age</span>
                  <span className="font-medium text-right">{girl.age} years old</span>
                </div>

                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Horse</span>
                  <span className="font-medium text-right">{girl.assignedHorse?.name || 'Not assigned'}</span>
                </div>

                {girl.boardPosition && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Board</span>
                    <span className="font-medium text-ggaa-primary text-right">🏆 {girl.boardPosition}</span>
                  </div>
                )}

                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Member since</span>
                  <span className="font-medium">{new Date(girl.memberSince).getFullYear()}</span>
                </div>

                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Graduation</span>
                  <span className="font-medium">{girl.graduationYear}</span>
                </div>

                {girl.emergencyContactPhone && (
                  <div className="pt-1">
                    <span className="text-gray-600 block mb-0.5">Emergency</span>
                    <a
                      href={`tel:${girl.emergencyContactPhone}`}
                      className="font-medium text-ggaa-green break-all"
                    >
                      {girl.emergencyContactName ? `${girl.emergencyContactName} · ` : ''}
                      {girl.emergencyContactPhone}
                    </a>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setSelectedGirl(girl)}
                className="mt-5 w-full bg-ggaa-green text-white py-3 px-4 rounded-lg hover:bg-ggaa-green/90 transition-colors text-sm font-medium min-h-[44px]"
              >
                Edit Profile
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Add Ranch Girl Modal */}
      {showAddForm && (
        <AddRanchGirlForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            fetchRanchGirls();
          }}
        />
      )}

      {/* Edit Ranch Girl Modal */}
      {selectedGirl && (
        <EditRanchGirlForm
          ranchGirl={selectedGirl}
          onClose={() => setSelectedGirl(null)}
          onSuccess={() => {
            setSelectedGirl(null);
            fetchRanchGirls();
          }}
        />
      )}
    </div>
  );
} 